import { NodeProps } from 'reactflow';
import { Label } from '@givebutter/primitives/label';
import { AddNoteFormValues, AddNoteNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { truncateText } from 'helpers/formatting';

interface AddNoteActionNodeProps extends NodeProps<AddNoteNodeData> {}
const AddNote = ({ id, selected, data: { formData, type } }: AddNoteActionNodeProps) => {
    const header = getActionNodeHeaderDetails(type);

    const methods = Form.useGivebutterForm<AddNoteFormValues>({
        mode: 'all',
        defaultValues: {
            note: formData?.values.note || '',
        },
    });

    const {
        control,
        formState: { errors },
        trigger,
        getValues,
    } = methods;
    const isError = Object.keys(errors).length > 0;

    useSyncFormToNode<AddNoteFormValues>({ nodeId: id, control });
    useDebouncedAutosave<AddNoteFormValues>({ methods });
    useTriggerValidationOnDeselection<AddNoteFormValues>({ trigger, selected });

    return (
        <CanvasNode.Root className="max-w-96" isSelected={selected} isError={isError} id={id}>
            <Form.Provider {...methods}>
                <StepFormContainer>
                    <CanvasNode.Container>
                        <CanvasNode.ErrorAlert />
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={header.icon} className="text-green-500" />
                            <CanvasNode.Title>{header.text}</CanvasNode.Title>
                            <DropdownMenu.Root>
                                <CanvasNode.ActionMenuTrigger />
                                <DropdownMenu.Content align="end">
                                    <DuplicateNodeMenuItem />
                                    <DeleteCanvasNodeMenuItem />
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <Form.Container>
                                    <Label>Date</Label>
                                    <p className="text-gray-600">
                                        Will be based on when the trigger occurs
                                    </p>
                                </Form.Container>

                                <Form.Container>
                                    <Form.UseFormLabel name="note">Notes</Form.UseFormLabel>
                                    <Form.Textarea
                                        control={control}
                                        name="note"
                                        rows={8}
                                        placeholder="Add note"
                                        rules={{ required: 'Note is required' }}
                                    />
                                    <Form.UseFormError name={'note'} />
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <AddNoteActionFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </StepFormContainer>
            </Form.Provider>
        </CanvasNode.Root>
    );
};

interface AddNoteActionFormValuesDisplayProps {
    values: AddNoteFormValues;
    showLabel?: boolean;
}
const AddNoteActionFormValuesDisplay = ({
    values,
    showLabel = false,
}: AddNoteActionFormValuesDisplayProps) => {
    const { note } = values;
    const actionHeader = getActionNodeHeaderDetails(WorkflowActionTypeEnum.ADD_NOTE);
    if (!actionHeader) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? (
                <p className="text-sm font-medium text-gray-900">{actionHeader.text}</p>
            ) : null}
            <p className="text-sm text-gray-500">{truncateText(note || '', 100)}</p>
        </div>
    );
};

export { AddNote, AddNoteActionFormValuesDisplay };
export type { AddNoteActionNodeProps };
